import {
  demoPurchasePlan as publicDemoPurchasePlan,
  withPublicInventoryDemo,
} from '../dev/inventory-demo-data'
import { withDemoUi } from '../dev/demoMode'
import { apiRequest } from './client'

export type PurchaseWindowDto = {
  days: number
  daily_usage: number | null
  drop_days: number
}

export type PurchaseOfferDto = {
  catalog_item_id: number | null
  product_type_id: number | null
  private_supplier_id: number | null
  supplier_name: string
  item_name: string
  price: number
  unit: string | null
  package_size: string | null
  quantity: number | null
  base_quantity_per_unit: number | null
  line_total: number | null
  recommended: boolean
  /** Когда приедет машина этого поставщика. */
  delivery_date: string
  /** Сколько дней расхода закрывает его поставка. */
  cover_days: number
  /** Объём в единицах учёта под срок поставки этого поставщика. */
  order_quantity: number | null
}

export type PurchaseLineDto = {
  store_external_id: string
  store_role: string
  product_external_id: string
  product_name: string
  unit: string
  on_hand: number
  windows: PurchaseWindowDto[]
  usage_window_days: number | null
  daily_usage: number | null
  usage_shifted: boolean
  insufficient_history: boolean
  order_quantity: number | null
  offers: PurchaseOfferDto[]
}

export type PurchasePlanDto = {
  order_date: string
  snapshot_date: string | null
  required_snapshot_date: string
  snapshot_synced_at: string | null
  stale: boolean
  cover_days: number
  stock_days: number
  delivery_same_day: boolean
  delivery_next_day: boolean
  delivery_weekends: boolean
  lines: PurchaseLineDto[]
}

/** Окна расхода, по которым считается медиана. null — выбрать самое длинное с историей. */
export const USAGE_WINDOW_DAYS = [7, 14, 21, 30, 60, 90] as const

export const purchasePlanPath = (
  organizationId: number,
  venueId: number,
  usageWindowDays?: number | null,
) => {
  const path = `/organizations/${organizationId}/venues/${venueId}/inventory/purchase`
  return usageWindowDays == null ? path : `${path}?usage_window_days=${usageWindowDays}`
}

const demoPurchasePlan = (): PurchasePlanDto => ({
  order_date: new Date().toISOString().slice(0, 10),
  snapshot_date: new Date().toISOString().slice(0, 10),
  required_snapshot_date: new Date().toISOString().slice(0, 10),
  snapshot_synced_at: new Date().toISOString(),
  stale: false,
  cover_days: 1,
  stock_days: 3,
  delivery_same_day: false,
  delivery_next_day: false,
  delivery_weekends: false,
  lines: [
    {
      store_external_id: 'demo-kitchen',
      store_role: 'KITCHEN',
      product_external_id: 'lime',
      product_name: 'Лайм',
      unit: 'кг',
      on_hand: 0.771,
      windows: [],
      usage_window_days: 90,
      daily_usage: 0.212,
      usage_shifted: false,
      insufficient_history: false,
      order_quantity: 0.077,
      offers: [
        {
          catalog_item_id: 101,
          product_type_id: 40,
          private_supplier_id: 1,
          supplier_name: 'Овощи Юг',
          item_name: 'Лайм',
          price: 210,
          unit: 'кг',
          package_size: null,
          quantity: 0.077,
          base_quantity_per_unit: 1,
          line_total: 16.17,
          recommended: true,
          delivery_date: new Date().toISOString().slice(0, 10),
          cover_days: 1,
          order_quantity: 0.077,
        },
        {
          catalog_item_id: 102,
          product_type_id: 40,
          private_supplier_id: 2,
          supplier_name: 'Фрукты Север',
          item_name: 'Лайм',
          price: 260,
          unit: 'кг',
          package_size: null,
          quantity: 0.288,
          base_quantity_per_unit: 1,
          line_total: 74.88,
          recommended: false,
          delivery_date: new Date(Date.now() + 86_400_000).toISOString().slice(0, 10),
          cover_days: 2,
          order_quantity: 0.288,
        },
      ],
    },
    {
      store_external_id: 'demo-kitchen',
      store_role: 'KITCHEN',
      product_external_id: 'fillet',
      product_name: 'Филе куриное',
      unit: 'кг',
      on_hand: 7.072,
      windows: [],
      usage_window_days: 90,
      daily_usage: 1.276,
      usage_shifted: false,
      insufficient_history: false,
      order_quantity: 0,
      offers: [],
    },
    {
      store_external_id: 'demo-kitchen',
      store_role: 'KITCHEN',
      product_external_id: 'flour',
      product_name: 'Пшеничная мука',
      unit: 'кг',
      on_hand: 31.266,
      windows: [],
      usage_window_days: 90,
      daily_usage: 0.566,
      usage_shifted: false,
      insufficient_history: false,
      order_quantity: 0,
      offers: [],
    },
    {
      store_external_id: 'demo-kitchen',
      store_role: 'KITCHEN',
      product_external_id: 'oil',
      product_name: 'Подсолнечное масло',
      unit: 'л',
      on_hand: 13.049,
      windows: [],
      usage_window_days: 90,
      daily_usage: 0.186,
      usage_shifted: false,
      insufficient_history: false,
      order_quantity: 0,
      offers: [],
    },
  ],
})

const loadPurchasePlanFromApi = (
  organizationId: number,
  venueId: number,
  usageWindowDays?: number | null,
) =>
  withDemoUi(demoPurchasePlan(), () =>
    apiRequest<PurchasePlanDto>(purchasePlanPath(organizationId, venueId, usageWindowDays)),
  )

export function loadPurchasePlan(
  organizationId: number,
  venueId: number,
  usageWindowDays?: number | null,
) {
  return withPublicInventoryDemo(
    () => publicDemoPurchasePlan({ organizationId, venueId }, usageWindowDays),
    () => loadPurchasePlanFromApi(organizationId, venueId, usageWindowDays),
  )
}
